import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import Constants from "../../../../common/Constants";
import { formatDate, formatDateTime } from "../../../../common/Validations";
import ItemService from "../../../../services/commonService";
import MasterList from "../../../common/MasterList";
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import { ErrorModal } from "../../../common/errorModal";
import { toastFunc } from "../../../common/util";
import RevisingNumberForm from "./RevisingNumberForm";
import "./RevisingNumber.scss";

const RevisingNumber = () => {
  const [records, setRecords] = useState([]);
  const [auditRecords, setAuditRecords] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const loadRecords = useCallback(async () => {
    const result = await ItemService.allCommonServices(
      {},
      Constants.MASTER.REVISING_NUMBER.list
    );
    if (result?.status === 200) {
      setRecords(result.data || []);
    } else {
      setError(result?.message || t("msg_master_list.msg_revising_number.load_error"));
    }
  }, [t]);

  const loadAudit = useCallback(async () => {
    const result = await ItemService.allCommonServices(
      {},
      Constants.MASTER.REVISING_NUMBER.AUDIT
    );
    if (result?.status === 200) {
      setAuditRecords(result.data || []);
    } else {
      setError(result?.message || t("msg_master_list.msg_revising_number.audit_error"));
    }
  }, [t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleOpen = (record) => {
    setSelectedRecord(record?._id ? record : null);
    setFormOpen(true);
  };

  const handleSave = async (record, isEdit) => {
    const method = isEdit
      ? Constants.MASTER.REVISING_NUMBER.UPDATE
      : Constants.MASTER.REVISING_NUMBER.ADD;
    const result = await ItemService.allCommonServices(record, method);
    if (result?.status === 200) {
      toastFunc(Constants.MASTER.TOAST.success, result.message, 2500);
      setFormOpen(false);
      setSelectedRecord(null);
      await loadRecords();
      if (showAudit) await loadAudit();
    } else {
      setError(result?.message || t("msg_master_list.msg_revising_number.save_error"));
    }
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;
    const result = await ItemService.allCommonServices(
      { _id: deleteRecord._id },
      Constants.MASTER.REVISING_NUMBER.DELETE
    );
    if (result?.status === 200) {
      toastFunc(Constants.MASTER.TOAST.success, result.message, 2500);
      setDeleteRecord(null);
      await loadRecords();
      if (showAudit) await loadAudit();
    } else {
      setDeleteRecord(null);
      setError(result?.message || t("msg_master_list.msg_revising_number.delete_error"));
    }
  };

  const toggleAudit = async () => {
    const nextValue = !showAudit;
    setShowAudit(nextValue);
    if (nextValue) await loadAudit();
  };

  const columns = [
    {
      name: t("msg_common.actions"),
      width: "110px",
      cell: (row) => (
        <>
          <i
            className="bi bi-pencil-square iconTable"
            title="Edit"
            onClick={() => handleOpen(row)}
          />
          {row.canDelete && (
            <i
              className="bi bi-trash3 iconTable"
              title={t("msg_common.delete")}
              onClick={() => setDeleteRecord(row)}
            />
          )}
        </>
      ),
    },
    {
      name: "Type of Report",
      selector: (row) => row.reportType,
      sortable: true,
      width: "190px",
      cell: (row) =>
        Constants.MASTER.REVISING_NUMBER.REPORT_TYPES.find(
          (option) => option.value === row.reportType
        )?.label || "Legacy / Not Assigned",
    },
    {
      name: t("msg_master_list.msg_revising_number.date"),
      selector: (row) => row.date,
      sortable: true,
      width: "160px",
      cell: (row) => formatDate(row.date),
    },
    {
      name: t("msg_master_list.msg_revising_number.revise_number"),
      selector: (row) => row.reviseNumber,
      sortable: true,
      width: "210px",
    },
    {
      name: t("msg_master_list.msg_revising_number.remarks"),
      selector: (row) => row.remarks,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: t("msg_master_list.msg_revising_number.created_by"),
      selector: (row) => row.createdByName,
      width: "170px",
    },
    {
      name: t("msg_common.created_date"),
      selector: (row) => row.createdDate,
      width: "210px",
      cell: (row) => formatDateTime(row.createdDate),
    },
    {
      name: t("msg_master_list.msg_revising_number.last_updated"),
      selector: (row) => row.updatedDate || row.createdDate,
      width: "210px",
      cell: (row) => formatDateTime(row.updatedDate || row.createdDate),
    },
  ];

  const getReportTypeLabel = (reportType) =>
    Constants.MASTER.REVISING_NUMBER.REPORT_TYPES.find(
      (option) => option.value === reportType
    )?.label || "Legacy / Not Assigned";

  const matchesReportTypeFilter = useCallback(
    (reportType) =>
      !reportTypeFilter ||
      (reportTypeFilter === "__LEGACY__"
        ? !reportType
        : reportType === reportTypeFilter),
    [reportTypeFilter]
  );

  const filteredRecords = useMemo(
    () => records.filter((record) => matchesReportTypeFilter(record.reportType)),
    [records, matchesReportTypeFilter]
  );

  const filteredAuditRecords = useMemo(
    () =>
      auditRecords.filter((record) =>
        matchesReportTypeFilter(
          record.newData?.reportType || record.previousData?.reportType
        )
      ),
    [auditRecords, matchesReportTypeFilter]
  );

  const auditColumns = [
    {
      name: t("msg_master_list.msg_revising_number.action"),
      selector: (row) => row.actionType,
      width: "130px",
    },
    {
      name: t("msg_master_list.msg_revising_number.record_id"),
      selector: (row) => row.revisingNumberRecordId,
      width: "230px",
    },
    {
      name: "Type of Report",
      selector: (row) =>
        row.newData?.reportType || row.previousData?.reportType || "",
      width: "190px",
      cell: (row) =>
        getReportTypeLabel(
          row.newData?.reportType || row.previousData?.reportType
        ),
    },
    {
      name: t("msg_master_list.msg_revising_number.previous_value"),
      selector: (row) => row.previousData?.reviseNumber || "-",
      width: "190px",
    },
    {
      name: t("msg_master_list.msg_revising_number.new_value"),
      selector: (row) => row.newData?.reviseNumber || "-",
      width: "190px",
    },
    {
      name: t("msg_master_list.msg_revising_number.user"),
      selector: (row) => `${row.userId || ""} ${row.userName || ""}`.trim(),
      width: "220px",
    },
    {
      name: t("msg_master_list.msg_revising_number.action_date"),
      selector: (row) => row.actionDate,
      width: "210px",
      cell: (row) => formatDateTime(row.actionDate),
    },
  ];

  return (
    <main className="dashboard main" id="main">
      <Row className="justify-center subContent">
        <Col xs={8} md={8} className="rightSide">
          <div className="rightSideInner timesheet">
            <h2 className="mainTitle">
              {t("msg_master_list.msg_revising_number.name")}
            </h2>
            <p className="subText">
              {t("msg_master_list.msg_revising_number.details")}
            </p>
          </div>
        </Col>
        <Col xs={4} md={4} className="rightSide text-end">
          <Button variant="outline-primary" onClick={toggleAudit}>
            <i className="bi bi-shield-check me-2" />
            {showAudit
              ? t("msg_master_list.msg_revising_number.show_records")
              : t("msg_master_list.msg_revising_number.show_audit")}
          </Button>
        </Col>
        <Col xs={12} className="revising-number-policy">
          <div className="revising-number-policy__icon" aria-hidden="true">
            <i className="bi bi-info-circle" />
          </div>
          <div className="revising-number-policy__content">
            <strong>Record maintenance rules</strong>
            <p>
              Type of Report controls which printed report uses the revision.
              All records can be edited; Delete is available only for an
              unused record that is not the first/default record for its
              report type. Records shown as Legacy / Not Assigned must be
              edited and assigned before the two report types can resolve
              them independently.
            </p>
            <div className="revising-number-policy__rules">
              <span>
                <i className="bi bi-lock-fill" aria-hidden="true" /> First
                record per report type: Edit only
              </span>
              <span>
                <i className="bi bi-link-45deg" aria-hidden="true" /> Used by
                a job card: Edit only
              </span>
              <span>
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                Unused later record: Edit or Delete
              </span>
            </div>
          </div>
        </Col>
        <Col xs={12} className="revising-number-filter-bar">
          <div className="revising-number-filter-bar__field">
            <Form.Label htmlFor="revising-number-report-filter">
              <i className="bi bi-funnel me-2" aria-hidden="true" />
              Filter by Type of Report
            </Form.Label>
            <Form.Select
              id="revising-number-report-filter"
              value={reportTypeFilter}
              onChange={(event) => setReportTypeFilter(event.target.value)}
              aria-label="Filter Revising Numbers by Type of Report"
            >
              <option value="">All Report Types</option>
              {Constants.MASTER.REVISING_NUMBER.REPORT_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {(records.some((record) => !record.reportType) ||
                auditRecords.some(
                  (record) =>
                    !record.newData?.reportType &&
                    !record.previousData?.reportType
                )) && (
                <option value="__LEGACY__">Legacy / Not Assigned</option>
              )}
            </Form.Select>
          </div>
          <span className="revising-number-filter-bar__count">
            {showAudit ? filteredAuditRecords.length : filteredRecords.length}{" "}
            {showAudit ? "audit actions" : "records"}
          </span>
        </Col>
        <hr />
      </Row>

      {showAudit ? (
        <MasterList
          title={t("msg_master_list.msg_revising_number.audit_title")}
          columnsData={auditColumns}
          rowsData={filteredAuditRecords}
          paginationRowsPerPageOptions={Constants.ROW_PER_PAGE}
          pagination
          cardHeader={false}
          selectableRows={false}
        />
      ) : (
        <MasterList
          columnsData={columns}
          rowsData={filteredRecords}
          paginationRowsPerPageOptions={Constants.ROW_PER_PAGE}
          pagination
          selectableRows={false}
          deleteEnabled={false}
          handleOpen={handleOpen}
        />
      )}

      {formOpen && (
        <RevisingNumberForm
          data={selectedRecord}
          show={formOpen}
          onHide={() => {
            setFormOpen(false);
            setSelectedRecord(null);
          }}
          onSave={handleSave}
        />
      )}

      {deleteRecord && (
        <ConfirmationModal
          show={Boolean(deleteRecord)}
          heading={t("msg_master_list.msg_revising_number.delete_heading")}
          bodyMessage={t("msg_master_list.msg_revising_number.delete_body", {
            reviseNumber: deleteRecord.reviseNumber,
          })}
          onDelete={handleDelete}
          onHide={() => setDeleteRecord(null)}
          type="delete"
        />
      )}

      {error && (
        <ErrorModal
          show={Boolean(error)}
          heading={t("msg_master_list.msg_revising_number.error_heading")}
          bodyMessage={error}
          onHide={() => setError("")}
        />
      )}
    </main>
  );
};

export default RevisingNumber;
